import { intervalToDuration } from 'date-fns';

type TableRow = (string | number | null)[];

/**
 * Interface for metric calculations requiring numerator and denominator
 */
interface MetricCalculation {
  numerator: number;
  denominator: number;
}

/**
 * Calculates average change as a percentage
 * Example: If numerator is 30 and denominator is 60, returns 50 (%)
 */
export const avgChange = ({
  numerator,
  denominator,
}: MetricCalculation): number => {
  if (numerator <= 0 || denominator <= 0) return 0;
  return Math.round(numerator / denominator);
};

/**
 * Calculates rate of change as a ratio
 * Example: If numerator is 30 and denominator is 60, returns 0.5
 */
export const rateOfChange = ({
  numerator,
  denominator,
}: MetricCalculation): number => {
  if (numerator <= 0 || denominator <= 0) return 0;
  return Math.round((numerator / denominator) * 100);
};

/**
 * Converts seconds to human-readable time format
 * Returns the largest applicable unit (years, months, days, hours, or minutes)
 * Example: 7200 seconds -> "2 hours"
 */
export const secondsToHumanTime = (data) => {
  if (!data || !isFinite(data) || isNaN(data)) {
    return '-';
  }

  const { minutes, hours, days, months, years, seconds } = intervalToDuration({
    start: 0,
    end: data * 1000,
  });
  if (years) {
    return `${years} year${years > 1 ? 's' : ''}`;
  }
  if (months) {
    return `${months} month${months > 1 ? 's' : ''}`;
  }
  if (days) {
    return `${days} day${days > 1 ? 's' : ''}`;
  }
  if (hours) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  }
  if (minutes) {
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  }
  if (seconds) {
    return `${seconds} second${seconds > 1 ? 's' : ''}`;
  }

  return '-';
};

export const processMetricValue = (
  value: number | null,
  unit?: string
): number | string => {
  if (value === null || value === undefined) return 0;
  if (unit === 'time') {
    const timeValue = secondsToHumanTime(value);
    // If the time value is too small or invalid, return 0 instead of '-'
    return timeValue === '-' ? 0 : timeValue;
  }
  return value;
};

/**
 * Gets the value from row for a specific header
 */
export const getValueForHeader = (
  headers: string[],
  row: TableRow | undefined,
  headerKey: string
): number | null => {
  const index = headers.indexOf(headerKey);
  return index !== -1 ? (row?.[index] as number | null) : null;
};
